import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  MenuIngredient,
  MenuIngredientMigrationApplyResult,
  MenuIngredientMigrationDecision,
  MenuIngredientMigrationPreview,
  menuIngredientService
} from '../services/menuIngredientService';
import { useAdminScope } from '../contexts/AdminScopeContext';
import {
  isMenuCatalogManagedByPosCaisse,
  MENU_CATALOG_POSCAISSE_MESSAGE,
} from '../utils/menuCatalogOwnership';

const MenuIngredients: React.FC = () => {
  const { selectedStore } = useAdminScope();
  const isCatalogReadOnly = isMenuCatalogManagedByPosCaisse(selectedStore);
  const [items, setItems] = useState<MenuIngredient[]>([]);
  const [label, setLabel] = useState('');
  const [aliases, setAliases] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [migrationPreview, setMigrationPreview] = useState<MenuIngredientMigrationPreview | null>(null);
  const [migrationResult, setMigrationResult] = useState<MenuIngredientMigrationApplyResult | null>(null);
  const [migrationDialogOpen, setMigrationDialogOpen] = useState(false);
  const [loadingMigration, setLoadingMigration] = useState(false);
  const [applyingMigration, setApplyingMigration] = useState(false);
  const [migrationDecisions, setMigrationDecisions] = useState<Record<string, MenuIngredientMigrationDecision>>({});

  const migrationWorkCount =
    (migrationPreview?.menuIngredientsToCreate.length ?? 0) +
    (migrationPreview?.productMappingsToCreate ?? 0);

  const seedMigrationDecisions = useCallback((preview: MenuIngredientMigrationPreview) => {
    setMigrationDecisions((current) => {
      const candidatesByKey = new Map(preview.menuIngredientsToCreate.map((candidate) => [candidate.normalizedName, candidate]));
      const next: Record<string, MenuIngredientMigrationDecision> = {};
      preview.menuIngredientsToCreate.forEach((candidate) => {
        const singularCandidate = candidate.normalizedName.endsWith('s')
          ? candidatesByKey.get(candidate.normalizedName.slice(0, -1))
          : undefined;
        next[candidate.normalizedName] = current[candidate.normalizedName] ?? {
          normalizedName: candidate.normalizedName,
          action: 'USE',
          targetLabel: singularCandidate?.label ?? candidate.label,
        };
      });
      return next;
    });
  }, []);

  const loadMigrationPreview = useCallback(async () => {
    setLoadingMigration(true);
    try {
      const preview = await menuIngredientService.previewLegacyImport();
      setMigrationPreview(preview);
      seedMigrationDecisions(preview);
    } catch (requestError: any) {
      setMigrationPreview(null);
      setMigrationDecisions({});
      if (!String(requestError?.message || '').includes('Sélectionnez un magasin')) {
        setError(requestError?.message || 'Analyse migration impossible.');
      }
    } finally {
      setLoadingMigration(false);
    }
  }, [seedMigrationDecisions]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await menuIngredientService.list(false));
      await loadMigrationPreview();
    } catch (requestError: any) {
      setError(requestError?.message || 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [loadMigrationPreview]);

  useEffect(() => {
    void load();
  }, [load]);

  const normalize = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const migrationTargetOptions = useMemo(() => {
    const values = new Map<string, string>();
    items.forEach((item) => {
      const key = normalize(item.label);
      if (key) values.set(key, item.label);
    });
    migrationPreview?.menuIngredientsToCreate.forEach((candidate) => {
      if (!values.has(candidate.normalizedName)) {
        values.set(candidate.normalizedName, candidate.label);
      }
    });
    return Array.from(values.values()).sort((left, right) => left.localeCompare(right));
  }, [items, migrationPreview]);

  const migrationDecisionSummary = useMemo(() => {
    const candidates = migrationPreview?.menuIngredientsToCreate ?? [];
    const targetNames = new Set<string>();
    let excluded = 0;
    let invalid = 0;

    candidates.forEach((candidate) => {
      const decision = migrationDecisions[candidate.normalizedName] ?? {
        normalizedName: candidate.normalizedName,
        action: 'USE',
        targetLabel: candidate.label,
      };
      if (decision.action === 'EXCLUDE') {
        excluded += 1;
        return;
      }
      const targetLabel = (decision.targetLabel ?? '').trim();
      const targetKey = normalize(targetLabel);
      if (!targetKey) {
        invalid += 1;
        return;
      }
      targetNames.add(targetKey);
    });

    return {
      excluded,
      invalid,
      targetCount: targetNames.size,
      used: candidates.length - excluded - invalid,
    };
  }, [migrationDecisions, migrationPreview]);

  const duplicateGroups = React.useMemo(() => {
    const groups = new Map<string, MenuIngredient[]>();
    items.forEach((item) => {
      [item.label, ...item.aliases].forEach((value) => {
        const key = normalize(value);
        if (!key) return;
        groups.set(key, [...(groups.get(key) ?? []), item]);
      });
    });
    return Array.from(groups.entries())
      .filter(([, values]) => new Set(values.map((item) => item.id)).size > 1)
      .map(([key, values]) => ({ key, values: Array.from(new Map(values.map((item) => [item.id, item])).values()) }));
  }, [items]);

  const create = async () => {
    if (!label.trim()) return;
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await menuIngredientService.create(
        label,
        aliases.split(',').map((item) => item.trim()).filter(Boolean),
      );
      setLabel('');
      setAliases('');
      await load();
    } catch (requestError: any) {
      setError(requestError?.message || 'Creation impossible.');
    } finally {
      setSaving(false);
    }
  };

  const applyMigration = async () => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    setApplyingMigration(true);
    setError(null);
    try {
      const decisions = (migrationPreview?.menuIngredientsToCreate ?? []).map((candidate) => (
        migrationDecisions[candidate.normalizedName] ?? {
          normalizedName: candidate.normalizedName,
          action: 'USE' as const,
          targetLabel: candidate.label,
        }
      ));
      const result = await menuIngredientService.applyLegacyImport(decisions);
      setMigrationResult(result);
      setMigrationPreview(result.remainingPreview);
      seedMigrationDecisions(result.remainingPreview);
      setItems(await menuIngredientService.list(false));
    } catch (requestError: any) {
      setError(requestError?.message || 'Migration impossible.');
    } finally {
      setApplyingMigration(false);
    }
  };

  const updateMigrationDecision = (
    normalizedName: string,
    patch: Partial<MenuIngredientMigrationDecision>,
  ) => {
    const candidate = migrationPreview?.menuIngredientsToCreate.find((item) => item.normalizedName === normalizedName);
    setMigrationDecisions((current) => ({
      ...current,
      [normalizedName]: {
        normalizedName,
        action: current[normalizedName]?.action ?? 'USE',
        targetLabel: current[normalizedName]?.targetLabel ?? candidate?.label ?? '',
        ...patch,
      },
    }));
  };

  const toggle = async (item: MenuIngredient, field: 'active' | 'removableDefault') => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    setSaving(true);
    try {
      await menuIngredientService.update(item.id, { [field]: !item[field] });
      await load();
    } finally {
      setSaving(false);
    }
  };

  const updateAliases = async (item: MenuIngredient, value: string) => {
    if (isCatalogReadOnly) {
      setError(MENU_CATALOG_POSCAISSE_MESSAGE);
      return;
    }
    setSaving(true);
    try {
      await menuIngredientService.update(item.id, {
        aliases: value.split(',').map((entry) => entry.trim()).filter(Boolean),
      });
      await load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Ingredients menu</Typography>
          <Typography color="text.secondary">Catalogue structure utilise par les produits et les customisations.</Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={() => void load()} disabled={loading}>Actualiser</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isCatalogReadOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {MENU_CATALOG_POSCAISSE_MESSAGE}
        </Alert>
      )}

      {(migrationWorkCount > 0 || migrationResult) && (
        <Paper sx={{ p: 2, mb: 3, border: '1px solid', borderColor: migrationWorkCount > 0 ? 'warning.light' : 'success.light' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Migration ingrédients des anciens produits
              </Typography>
              <Typography color="text.secondary">
                {migrationWorkCount > 0
                  ? `${migrationPreview?.productsWithLegacyIngredients ?? 0} produits anciens à relier au catalogue structuré.`
                  : 'Aucune migration nécessaire pour le magasin actif.'}
              </Typography>
              {migrationResult && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  Migration terminée: {migrationResult.createdMenuIngredients} ingrédients créés, {migrationResult.updatedProducts} produits mis à jour.
                </Alert>
              )}
            </Box>
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'stretch', md: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => void loadMigrationPreview()}
                disabled={loadingMigration || applyingMigration}
              >
                Analyser
              </Button>
              {migrationWorkCount > 0 && (
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<AutoFixHighIcon />}
                  onClick={() => setMigrationDialogOpen(true)}
                  disabled={loadingMigration || applyingMigration || isCatalogReadOnly}
                >
                  Voir migration
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
          <TextField
            label="Nouvel ingredient menu"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            fullWidth
            size="small"
            disabled={isCatalogReadOnly}
          />
          <TextField
            label="Alias"
            value={aliases}
            onChange={(event) => setAliases(event.target.value)}
            helperText="Séparés par virgule"
            fullWidth
            size="small"
            disabled={isCatalogReadOnly}
          />
          <Tooltip title={label.trim() ? 'Ajouter l’ingredient menu' : 'Saisir un libelle avant ajout'}>
            <span>
              <IconButton
                color="primary"
                onClick={() => void create()}
                disabled={saving || !label.trim() || isCatalogReadOnly}
                aria-label="Ajouter l’ingredient menu"
                sx={{
                  width: 40,
                  height: 40,
                  border: '1px solid',
                  borderColor: 'primary.main',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    borderColor: 'primary.dark',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'action.disabledBackground',
                    borderColor: 'divider',
                  },
                }}
              >
                {saving ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      {duplicateGroups.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Doublons possibles: {duplicateGroups.slice(0, 5).map((group) => (
            `${group.key} (${group.values.map((item) => item.label).join(' / ')})`
          )).join(' · ')}
        </Alert>
      )}

      <Paper>
        {loading ? (
          <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Libelle</TableCell>
                <TableCell>Cle</TableCell>
                <TableCell>Alias</TableCell>
                <TableCell>Retirable</TableCell>
                <TableCell>Statut</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.label}</TableCell>
                  <TableCell>{item.normalizedName}</TableCell>
                  <TableCell sx={{ minWidth: 240 }}>
                    <TextField
                      size="small"
                      defaultValue={item.aliases.join(', ')}
                      onBlur={(event) => void updateAliases(item, event.target.value)}
                      disabled={saving || isCatalogReadOnly}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={item.removableDefault}
                      onChange={() => void toggle(item, 'removableDefault')}
                      disabled={saving || isCatalogReadOnly}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip size="small" color={item.active ? 'success' : 'default'} label={item.active ? 'Actif' : 'Inactif'} />
                      <Switch checked={item.active} onChange={() => void toggle(item, 'active')} disabled={saving || isCatalogReadOnly} />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>Aucun ingredient menu.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={migrationDialogOpen} onClose={() => !applyingMigration && setMigrationDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Migration des anciens ingrédients produits</DialogTitle>
        <DialogContent>
          {loadingMigration && !migrationPreview ? (
            <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>
          ) : migrationPreview ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert severity="info">
                Cet assistant transforme les anciens champs produits en ingrédients menu structurés. Il est utile seulement pendant la migration; ensuite les nouveaux ingrédients se gèrent depuis cette page.
              </Alert>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Chip label={`${migrationPreview.totalProducts} produits analysés`} />
                <Chip label={`${migrationPreview.productsWithLegacyIngredients} produits concernés`} color={migrationPreview.productsWithLegacyIngredients > 0 ? 'warning' : 'success'} />
                <Chip label={`${migrationPreview.menuIngredientsToCreate.length} ingrédients à créer`} color={migrationPreview.menuIngredientsToCreate.length > 0 ? 'warning' : 'success'} />
                <Chip label={`${migrationPreview.productMappingsToCreate} liens à corriger`} color={migrationPreview.productMappingsToCreate > 0 ? 'warning' : 'success'} />
                {migrationPreview.ignoredProducts > 0 && (
                  <Chip label={`${migrationPreview.ignoredProducts} produits simples ignorés`} variant="outlined" />
                )}
              </Stack>

              {migrationPreview.menuIngredientsToCreate.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Nettoyage avant migration</Typography>
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    Corrigez les fautes et ignorez les valeurs qui ne sont pas de vrais ingrédients. Si deux lignes ont le même nom final, elles seront fusionnées automatiquement.
                  </Alert>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                    <Chip label={`${migrationDecisionSummary.used} libellés gardés`} color="success" variant="outlined" />
                    <Chip label={`${migrationDecisionSummary.targetCount} ingrédients finaux`} color="primary" variant="outlined" />
                    <Chip label={`${migrationDecisionSummary.excluded} ignorés`} color={migrationDecisionSummary.excluded > 0 ? 'warning' : 'default'} variant="outlined" />
                    {migrationDecisionSummary.invalid > 0 && (
                      <Chip label={`${migrationDecisionSummary.invalid} noms invalides`} color="error" />
                    )}
                  </Stack>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Libellé détecté</TableCell>
                          <TableCell>Décision</TableCell>
                          <TableCell>Nom final / fusion</TableCell>
                          <TableCell>Impact</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {migrationPreview.menuIngredientsToCreate.map((candidate) => {
                          const decision = migrationDecisions[candidate.normalizedName] ?? {
                            normalizedName: candidate.normalizedName,
                            action: 'USE' as const,
                            targetLabel: candidate.label,
                          };
                          const targetLabel = decision.targetLabel ?? candidate.label;
                          const targetKey = normalize(targetLabel);
                          const isMerged = decision.action === 'USE' && targetKey && targetKey !== candidate.normalizedName;

                          return (
                            <TableRow key={candidate.normalizedName} hover>
                              <TableCell sx={{ minWidth: 180 }}>
                                <Typography sx={{ fontWeight: 700 }}>{candidate.label}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {candidate.productCount} produit{candidate.productCount > 1 ? 's' : ''}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ minWidth: 220 }}>
                                <ToggleButtonGroup
                                  size="small"
                                  exclusive
                                  value={decision.action}
                                  onChange={(_, value) => {
                                    if (!value) return;
                                    updateMigrationDecision(candidate.normalizedName, {
                                      action: value,
                                      targetLabel: value === 'EXCLUDE' ? targetLabel : (targetLabel || candidate.label),
                                    });
                                  }}
                                >
                                  <ToggleButton value="USE">Créer / fusionner</ToggleButton>
                                  <ToggleButton value="EXCLUDE">Ignorer</ToggleButton>
                                </ToggleButtonGroup>
                              </TableCell>
                              <TableCell sx={{ minWidth: 260 }}>
                                <Autocomplete
                                  freeSolo
                                  size="small"
                                  options={migrationTargetOptions}
                                  value={targetLabel}
                                  disabled={decision.action === 'EXCLUDE'}
                                  onInputChange={(_, value) => updateMigrationDecision(candidate.normalizedName, {
                                    action: 'USE',
                                    targetLabel: value,
                                  })}
                                  onChange={(_, value) => updateMigrationDecision(candidate.normalizedName, {
                                    action: 'USE',
                                    targetLabel: String(value ?? candidate.label),
                                  })}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Nom final"
                                      error={decision.action === 'USE' && !targetKey}
                                      helperText={decision.action === 'EXCLUDE' ? 'Non ajouté aux produits' : 'Même nom = création, nom existant = fusion'}
                                    />
                                  )}
                                />
                              </TableCell>
                              <TableCell sx={{ minWidth: 180 }}>
                                {decision.action === 'EXCLUDE' ? (
                                  <Chip size="small" color="warning" label="Supprimé des compositions" />
                                ) : isMerged ? (
                                  <Chip size="small" color="primary" label={`Fusion vers ${targetLabel}`} />
                                ) : (
                                  <Chip size="small" color="success" label="Créer ou relier" />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Produit</TableCell>
                      <TableCell>Ingrédients détectés</TableCell>
                      <TableCell align="right">Déjà liés</TableCell>
                      <TableCell align="right">À corriger</TableCell>
                      <TableCell>Statut</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {migrationPreview.products.slice(0, 120).map((product) => (
                      <TableRow key={product.productId} hover>
                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>{product.productName}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.disabled' }}>{product.productId}</Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                            {product.legacyLabels.map((label) => (
                              <Chip key={label} size="small" label={label} />
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">{product.existingMappingCount}</TableCell>
                        <TableCell align="right">{product.mappingsToCreate}</TableCell>
                        <TableCell>
                          {product.ignoredReason ? (
                            <Chip size="small" label="Ignoré" variant="outlined" />
                          ) : product.mappingsToCreate > 0 ? (
                            <Chip size="small" color="warning" label="À migrer" />
                          ) : (
                            <Chip size="small" color="success" label="OK" />
                          )}
                          {product.ignoredReason && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {product.ignoredReason}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {migrationPreview.products.length > 120 && (
                <Typography variant="caption" color="text.secondary">
                  {migrationPreview.products.length - 120} produits supplémentaires masqués dans l’aperçu.
                </Typography>
              )}
            </Stack>
          ) : (
            <Alert severity="warning">Aucun aperçu disponible.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMigrationDialogOpen(false)} disabled={applyingMigration}>Fermer</Button>
          <Button onClick={() => void loadMigrationPreview()} disabled={loadingMigration || applyingMigration}>Réanalyser</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => void applyMigration()}
            disabled={
              applyingMigration ||
              loadingMigration ||
              isCatalogReadOnly ||
              migrationWorkCount === 0 ||
              migrationDecisionSummary.invalid > 0
            }
            startIcon={applyingMigration ? <CircularProgress size={18} color="inherit" /> : <AutoFixHighIcon />}
          >
            Appliquer le plan
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MenuIngredients;
